import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link as ReactLink } from 'react-router-dom';
import { Form } from '@unform/web';
import { FormHandles } from '@unform/core';
import * as Yup from 'yup';
import InputMask from 'react-input-mask';
import { Fallback } from '../../components/Fallback';
import Button from '../../components/Button';
import Card from '../../components/Card';
import Checkbox from '../../components/Checkbox';
import Input from '../../components/Input';
import Link from '../../components/Link';
import ModalValidatedCoupon from '../../components/ModalValidatedCoupon';
import ModalAcceptTerm from '../../components/ModalAcceptTerm';
import Select from '../../components/Select';

import { superaApi, beneficiaryApi } from '../../services';

import {
  cpfCnpj,
  delay,
  getBrasilUFs,
  getConfiguration,
  getValidationErrors,
  getYesOrNo,
  throwException,
  validateCPF,
  validateDate,
} from '../../utils';

import { Result } from '../../@types/apiResult';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../hooks/useToast';
import { useRules } from '../../hooks/useRules';
import { ValidationError } from '../../errors/ValidationError';

import supera from '../../assets/supera_v2.png';

import {
  Container,
  Animation,
  LoginArea,
  CardContent,
  FormContent,
  Row,
  AcceptTerms,
  Actions,
} from './styles';

interface ICupom {
  id: number;
  nrCoupon: string;
  cpf: string;
  crm: string;
  acceptanceTerm: boolean;
  createdAt: Date;
  excel: string;
  presentation: number;
  sku: string;
  cpfComprador: string;
  dataNascimento: string;
  dataNascimentoComprador: string;
}

interface RegisterFormData {
  hasCoupon: boolean;
  nrCoupon: number;
  cpf: string;
  crm: string;
  acceptanceTerm: [string];
  uf: string;
  presentation: number;
  sku: string;
  cpfComprador: string;
  dataNascimento: '';
  dataNascimentoComprador: '';
}

interface PresentationItem {
  label: string;
  value: string;
  product: string;
  skuId: string;
}

const Register: React.FC = () => {
  const { refreshToken } = useAuth();
  const { addToast } = useToast();
  const { hasCouponEnabled } = useRules();

  const formRef = useRef<FormHandles>(null);
  const selectYesOrNoRef = useRef<any>(null);
  const selectPresentationRef = useRef<any>(null);
  const selectUFRef = useRef<any>(null);

  const [loading, setLoading] = useState<boolean>(false);
  const [block, setblock] = useState<boolean>(true);
  const [firtTime, setFirstTime] = useState<boolean>(true);
  const [fallback, setFallback] = useState<boolean>(false);
  const [hasCoupon, setHasCoupon] = useState<boolean>(true);

  const [presentations, setPresentation] = useState<PresentationItem[]>([]);
  const [cupom, setCupom] = useState('');
  const [skuId, setSkuId] = useState('');
  const [localDate, setLocalDate] = useState('');
  const [localDateComprador, setLocalDateComprador] = useState('');
  const [hasError, setHasError] = useState(false);
  const [cpfPaciente, setCpfPaciente] = useState('');
  const [cpfCompradorValue, setCpfComprador] = useState('');
  const [crmValue, setCrmValue] = useState('');
  const [isAdultValidError, setIsAdultValidError] = useState<boolean>(false);

  const handleCpfPacienteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setCpfPaciente(value);
    formRef.current?.setFieldValue('cpf', value);
  };

  const handleCpfCompradorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setCpfComprador(value);
    formRef.current?.setFieldValue('cpfComprador', value);
  };

  const handleCrmChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setCrmValue(value);
    formRef.current?.setFieldValue('crm', value);
  };

  const [isSuccessShow, setIsSuccessShow] = useState<boolean>(false);
  const [isAcceptTermsShow, setIsAcceptTermsShow] = useState<boolean>(false);

  const toggleShowSuccess = useCallback(() => {
    setIsSuccessShow(!isSuccessShow);
    formRef.current?.reset();
    formRef.current?.setData({
      hasCoupon: false,
      nrCoupon: null,
      cpf: null,
      crm: null,
      acceptanceTerm: '',
      uf: '',
      presentations: null,
      sku: null,
      cpfComprador: null,
      dataNascimento: null,
      dataNascimentoComprador: null,
    });

    selectUFRef.current?.clear();
    selectYesOrNoRef.current?.clear();
    selectPresentationRef.current?.clear();
  }, [isSuccessShow]);

  const [cpfValidation, setCpfValidation] = useState({
    isValid: false,
    isLoading: false,
    error: '',
  });

  const formatDate = (value: string): string => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 2) return digits;
    if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
    return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 8)}`;
  };

  const toggleShowAcceptTerm = useCallback(() => {
    setIsAcceptTermsShow(!isAcceptTermsShow);
  }, [isAcceptTermsShow]);

  const handleChangeHasCoupon = useCallback((e: any) => {
    setHasCoupon(e.value === '1');
  }, []);

  async function getPresentation() {
    if (cupom !== '' && cupom.length === 15) {
      try {
        setblock(true);
        setFallback(true);
        setSkuId('');
        const token = await refreshToken();
        const response = await superaApi.get<Result<any>>(
          `api/v1/Beneficiary/GetAppresentation/${cupom}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const { data } = response.data;

        if (response.data.error !== null) {
          throw new Error(response.data.messages[0].message);
        }

        const removeDuplicates = (
          items: PresentationItem[],
        ): PresentationItem[] => {
          const seen = new Map<string | number, PresentationItem>();
          items.forEach(item => {
            if (!seen.has(item.skuId)) {
              seen.set(item.skuId, item);
            }
          });
          return Array.from(seen.values());
        };

        const dadosSemDuplicados: PresentationItem[] = removeDuplicates(data);

        setPresentation(dadosSemDuplicados);
        setblock(false);
        setFallback(false);
      } catch (err) {
        addToast({
          type: 'error',
          title: 'Cupom inválido!',
          description: 'Erro encontrado na validação do cupom.',
        });
        setFallback(false);
      }
    }
  }

  const formatDateForAPI = (date: string): string => {
    if (!date) return '';
    const [day, month, year] = date.split('/');
    return `${year}-${month}-${day}`; // Formato ISO (YYYY-MM-DD)
  };

  const getProductOptions = () => {
    return presentations.map(item => ({
      label: item.label,
      value: item.skuId,
      originalValue: item.value,
    }));
  };

  const getFirstProduct = () => {
    return presentations[0]?.product || '';
  };

  const getFirstValue = () => {
    return presentations[0]?.value || '';
  };

  useEffect(() => {
    if (!firtTime) {
      setSkuId('');
      setPresentation([]);
      getPresentation();
    } else {
      setFirstTime(false);
    }
  }, [cupom]);

  const validateCpf = async (cpf: string) => {
    try {
      setFallback(true);
      const token = await refreshToken();

      setCpfValidation(prev => ({ ...prev, isLoading: true, error: '' }));

      const cleanedCpf = cpf.replace(/\D/g, '');
      const response = await superaApi.get<Result<any>>(
        `api/v1/Coupons/VerifyPerson/${cleanedCpf}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      const { data } = response.data;
      if (response.data.error !== null) {
        setHasError(true);
        throw new Error(response.data.messages[0].message);
      }

      if (response.data.error !== null || response.data.success === false) {
        setHasError(true);
        addToast({
          type: 'error',
          title: 'Adesão restrita!',
          description:
            response.data.messages[0]?.message ||
            'Usuário reconhecido como funcionário.',
        });
        return false;
      }

      setFallback(false);

      setCpfValidation({
        isValid: data.valid,
        isLoading: false,
        error: data.valid ? '' : 'CPF inválido',
      });
      setFallback(false);
      setHasError(false);

      return true;
    } catch (error) {
      setHasError(true);
      addToast({
        type: 'error',
        title: 'Adesão restrita!',
        description: 'Usuário reconhecido como funcionário.',
      });

      setFallback(false);
      setHasError(true);

      return false;
    }
  };

  const ValidAdult = (dataNascimento: string) => {
    const birthDate = new Date(dataNascimento);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }

    if (age < 18) {
      return true;
    }

    return false;
  };

  const validateFakePerson = async (cpf: string, dataNascimento: string) => {
    try {
      setFallback(true);
      const token = await refreshToken();

      const validAdult = ValidAdult(dataNascimento);

      if (validAdult) {
        setHasError(true);
        addToast({
          type: 'error',
          title: 'Dados inválidos!',
          description: 'É necessário um comprador maior de idade.',
        });
        setIsAdultValidError(true);
        setFallback(false);
        return false;
      }

      setCpfValidation(prev => ({ ...prev, isLoading: true, error: '' }));

      const cleanedCpf = cpf.replace(/\D/g, '');
      const validDateNascimento = formatDateForAPI(dataNascimento);

      const response = await superaApi.get<Result<any>>(
        `api/v1/Coupons/VerifyPerson/${cleanedCpf}/?dataNascimento=${validDateNascimento}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      const { data } = response.data;

      if (response.data.error !== null && response.data.success === false) {
        throw new Error(response.data.messages[0].message);
      }

      if (response.data.error !== null || response.data.success === false) {
        setHasError(true);
        addToast({
          type: 'error',
          title: 'Dados inválidos!',
          description:
            response.data.messages[0]?.message ||
            'Dados informados não coincidem.',
        });
        return false;
      }

      setFallback(false);

      setCpfValidation({
        isValid: data.valid,
        isLoading: false,
        error: data.valid ? '' : 'CPF inválido',
      });
      setFallback(false);

      return true;
    } catch (error) {
      setHasError(true);
      addToast({
        type: 'error',
        title: 'Dados inválidos!',
        description: 'Dados informados não coincidem.',
      });
      setFallback(false);

      return false;
    }
  };

  const handleSubmit = useCallback(
    async (formData: RegisterFormData) => {
      try {
        if (formData.cpf === '' || formData.cpf == null) {
          setIsAdultValidError(false);
          setHasError(true);
          addToast({
            type: 'error',
            title: 'Dados inválidos!',
            description: 'É necessário preencher os dados do paciente.',
          });
          setFallback(false);
          return;
        }

        const validAdultPacient = ValidAdult(localDate);
        if (validAdultPacient) {
          if (localDateComprador === null || localDateComprador === '') {
            setIsAdultValidError(true);
            setHasError(true);
            addToast({
              type: 'error',
              title: 'Dados inválidos!',
              description: 'É necessário um comprador maior de idade.',
            });
            setFallback(false);
            return;
          }

          const validAdult = ValidAdult(localDateComprador);
          if (validAdult) {
            setIsAdultValidError(false);
            setHasError(true);
            addToast({
              type: 'error',
              title: 'Dados inválidos!',
              description: 'É necessário um comprador maior de idade.',
            });
            setFallback(false);
            return;
          }
          setIsAdultValidError(false);
        }

        const isCpfValid = await validateCpf(formData.cpf.replace(/\D/g, ''));

        if (!isCpfValid) {
          return;
        }

        if (formData.cpfComprador !== '') {
          const isCpfCompradorValid = await validateCpf(
            formData.cpfComprador.replace(/\D/g, ''),
          );
          if (!isCpfCompradorValid) {
            return;
          }
          const isBuyerDataValid = await validateFakePerson(
            formData.cpfComprador.replace(/\D/g, ''),
            localDateComprador,
          );
          if (!isBuyerDataValid) {
            return;
          }
        }
        const updatedToken = await refreshToken();

        if (
          formData.crm === '' ||
          formData.uf === '' ||
          formData.sku === '' ||
          formData.cpf === '' ||
          localDate === '' ||
          formData.acceptanceTerm.length < 1
        ) {
          addToast({
            type: 'error',
            title: 'Erro de requisição',
            description: 'Campos necessários não preenchidos.',
          });
          return;
        }

        setLoading(true);

        formRef.current?.setErrors({});

        let hasCouponObjectValidation = {};

        if (hasCouponEnabled) {
          hasCouponObjectValidation = {
            hasCoupon: Yup.string().required(
              'Por favor selecione a opção se possui um cupom!',
            ),
            nrCoupon: Yup.string().when('hasCoupon', {
              is: '1',
              then: Yup.string().required('O campo cupom é obrigatório!'),
            }),
          };
        } else {
          hasCouponObjectValidation = {
            nrCoupon: Yup.string().required('O campo cupom é obrigatório!'),
            presentation: Yup.string().required(
              'O campo produto é obrigatório!',
            ),
          };
        }

        const schema = Yup.object().shape({
          ...hasCouponObjectValidation,
          cpf: Yup.string().required('O campo CPF é obrigatório!'),
          crm: Yup.string().required('O campo CRM é obrigatório!'),
          uf: Yup.string().required('O campo UF é obrigatório!'),
          acceptanceTerm: Yup.boolean().oneOf(
            [true],
            'Por favor, leia e aceite os termos!',
          ),
        });

        validateCPF('cpf', 'Digite um CPF válido!', formData.cpf);

        const headers = { Authorization: `Bearer ${updatedToken}` };

        const {
          nrCoupon,
          cpf: formCpf,
          crm,
          acceptanceTerm: acceptanceTermForm,
          uf,
          cpfComprador: formCpfComprador,
          dataNascimento: formDataNascimento,
          dataNascimentoComprador: formDataNascimentoComprador,
        } = formData;

        const presentationValue = getFirstValue();
        const skuValue = skuId;
        const dataNascimentoValue = localDate || formDataNascimento;
        const dataNascimentoCompradorValue =
          localDateComprador || formDataNascimentoComprador;

        await delay(3000);

        const cpf = formCpf.replace(/\D/g, '');
        const cpfComprador = formCpfComprador.replace(/\D/g, '');

        const url = await getConfiguration('Supera.Coupons');
        const acceptanceTerm = acceptanceTermForm[0] === '1';

        const validDateNascimento = formatDateForAPI(dataNascimentoValue);
        const validDateNascimentoComprador = formatDateForAPI(
          dataNascimentoCompradorValue,
        );

        if (hasCoupon) {
          const response = await superaApi.put<Result<ICupom>>(
            url,
            {
              nrCoupon,
              cpf,
              crm,
              acceptanceTerm,
              uf,
              presentation: getFirstValue(),
              sku: skuId,
              dataNascimento: validDateNascimento,
              cpfComprador,
              dataNascimentoComprador: validDateNascimentoComprador,
            },
            { headers },
          );

          throwException(response.data);
        }

        addToast({
          type: 'success',
          title: 'Cupom validado com sucesso!',
          description:
            'Perfeito, seu cupom já foi validado e já pode ser usado.',
        });

        setLoading(false);
        setIsSuccessShow(true);

        formRef.current?.reset();
        formRef.current?.setData({
          hasCoupon: false,
          nrCoupon: null,
          cpf: null,
          crm: null,
          acceptanceTerm: '',
          uf: '',
          presentations: null,
          sku: null,
          dataNascimento: null,
          dataNascimentoComprador: null,
          cpfComprador: null,
        });

        selectUFRef.current?.clear();
        selectYesOrNoRef.current?.clear();
        selectPresentationRef.current?.clear();
        handleFinalizar();
      } catch (err: any) {
        setLoading(false);
        setblock(false);
        const isValidationError =
          err instanceof Yup.ValidationError || err instanceof ValidationError;

        if (isValidationError) {
          const errors = getValidationErrors(err as Yup.ValidationError);

          formRef.current?.setErrors(errors);

          return;
        }

        addToast({
          type: 'error',
          title: 'Falha na validação de cupom!',
          description: err.message,
        });
      }
    },
    [
      addToast,
      hasCouponEnabled,
      refreshToken,
      hasCoupon,
      skuId,
      localDate,
      localDateComprador,
    ],
  );

  const handleFinalizar = () => {
    formRef.current?.reset();

    setCupom('');
    setSkuId('');
    setLocalDate('');
    setLocalDateComprador('');
    setHasError(false);
    setPresentation([]);
    setCpfPaciente('');
    setCpfComprador('');
    setCrmValue('');

    formRef.current?.setFieldValue('cpf', '');
    formRef.current?.reset();
    formRef.current?.setData({
      hasCoupon: false,
      nrCoupon: '',
      cpf: '',
      crm: '',
      acceptanceTerm: [''],
      uf: '',
      presentation: '',
      sku: '',
      cpfComprador: '',
      dataNascimento: '',
      dataNascimentoComprador: '',
    });

    selectUFRef.current?.clear();
    selectYesOrNoRef.current?.clear();
    selectPresentationRef.current?.clear();

    const inputs = document.querySelectorAll('input');
    inputs.forEach(input => {
      input.blur(); // Remove o foco
      input.style.borderColor = '#d5d3d3'; // Cor padrão da borda
      input.style.boxShadow = 'none'; // Remove sombra
    });

    // Fecha o modal de sucesso
    setIsSuccessShow(false);
  };

  return (
    <Container>
      <ModalValidatedCoupon show={isSuccessShow} toggle={toggleShowSuccess} />
      <ModalAcceptTerm show={isAcceptTermsShow} toggle={toggleShowAcceptTerm} />

      <Animation>
        <Card>
          <Form ref={formRef} onSubmit={handleSubmit}>
            <CardContent>
              <ReactLink to="/">
                <img src={supera} alt="Supera" />
              </ReactLink>

              <p>Preencha os dados abaixo para validar o seu cupom.</p>

              <FormContent>
                <>
                  <Input
                    name="nrCoupon"
                    placeholder="Número do cupom"
                    maxLength={15}
                    onChange={e => setCupom(e.target.value)}
                    value={cupom}
                  />
                  <Fallback isActive={fallback} />
                  <Input
                    name="presentation"
                    placeholder="Produtos"
                    value={getFirstProduct()}
                    readOnly
                  />
                  <Select
                    name="sku"
                    options={getProductOptions()}
                    placeholder="Benefício"
                    loadingMessage="Pesquisando Produtos"
                    isDisabled={block}
                    ref={selectPresentationRef}
                    // onChange={(e: any) => setSkuId(e.value)}
                    value={
                      skuId
                        ? {
                            value: skuId,
                            label:
                              presentations.find(p => p.skuId === skuId)
                                ?.label || '',
                          }
                        : null
                    }
                    onChange={(e: any) => {
                      setSkuId(e.value);
                      formRef.current?.setFieldValue('sku', e.value);
                    }}
                    key={`sku-select-${cupom}`}
                  />
                  <input
                    type="hidden"
                    name="presentationValue"
                    value={getFirstValue()}
                  />
                </>
                <Input
                  name="cpf"
                  value={cpfPaciente}
                  onChange={handleCpfPacienteChange}
                  placeholder="CPF do paciente"
                  maxLength={14}
                  maskPattern={cpfCnpj}
                  onBlur={async (e: React.FocusEvent<HTMLInputElement>) => {
                    setCpfPaciente(e.target.value);
                    formRef.current?.setFieldValue('cpf', e.target.value);
                    const cpf = e.target.value.replace(/\D/g, '');
                    if (cpf.length === 11) {
                      await validateCpf(cpf);
                    }
                  }}
                />
                <InputMask
                  name="dataNascimento"
                  type="text"
                  placeholder="Data de nascimento paciente"
                  mask="99/99/9999"
                  maskChar={null}
                  value={localDate}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const { value } = e.target;
                    const rawValue = value.replace(/\D/g, '');
                    setLocalDate(value);
                    if (rawValue.length <= 8) {
                      formRef.current?.setFieldValue('dataNascimento', value);
                    }
                  }}
                  onBlur={async (e: React.FocusEvent<HTMLInputElement>) => {
                    const rawValue = e.target.value.replace(/\D/g, '');
                    if (rawValue.length === 8) {
                      const formattedValue = formatDate(rawValue);
                      formRef.current?.setFieldValue(
                        'dataNascimento',
                        formattedValue,
                      );

                      const cpf = formRef.current
                        ?.getFieldValue('cpf')
                        ?.replace(/\D/g, '');
                      if (cpf && cpf.length === 11) {
                        await validateFakePerson(cpf, formattedValue);
                      }
                    } else if (rawValue.length > 0) {
                      formRef.current?.setFieldError(
                        'dataNascimento',
                        'Data incompleta',
                      );
                    }
                  }}
                  style={{
                    width: '100%',
                    height: '40px',
                    padding: '12px 16px',
                    fontSize: '15px',
                    border: '1px solid #d5d3d3',
                    borderRadius: '50px',
                    fontFamily: 'Arial, sans-serif',
                    transition: 'all 0.3s ease',
                    outline: 'none',
                    marginBottom: '8px',
                  }}
                />

                <Input
                  name="cpfComprador"
                  value={cpfCompradorValue}
                  onChange={handleCpfCompradorChange}
                  placeholder="CPF do comprador"
                  maxLength={14}
                  maskPattern={cpfCnpj}
                  onBlur={async (e: React.FocusEvent<HTMLInputElement>) => {
                    setCpfComprador(e.target.value);
                    formRef.current?.setFieldValue(
                      'cpfComprador',
                      e.target.value,
                    );
                    const cpf = e.target.value.replace(/\D/g, '');
                    if (cpf.length === 11) {
                      await validateCpf(cpf);
                    }
                  }}
                />

                <InputMask
                  name="dataNascimentoComprador"
                  type="text"
                  placeholder="Data de nascimento comprador"
                  mask="99/99/9999"
                  maskChar={null}
                  value={localDateComprador}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const { value } = e.target;
                    const rawValue = value.replace(/\D/g, '');
                    setLocalDateComprador(value);
                    if (rawValue.length <= 8) {
                      formRef.current?.setFieldValue(
                        'dataNascimentoComprador',
                        value,
                      );
                    }
                  }}
                  onBlur={async (e: React.FocusEvent<HTMLInputElement>) => {
                    const dataNascimento = e.target.value;
                    const formattedValue = validateDate(e.target.value);
                    formRef.current?.setFieldValue(
                      'dataNascimentoComprador',
                      formattedValue,
                    );
                    if (dataNascimento && dataNascimento.length === 10) {
                      const cpf = formRef.current
                        ?.getFieldValue('cpfComprador')
                        ?.replace(/\D/g, '');
                      await validateFakePerson(cpf, dataNascimento);
                    }
                  }}
                  onMouseEnter={(e: React.MouseEvent<HTMLInputElement>) => {
                    e.currentTarget.style.borderColor = '#d5d3d3';
                    e.currentTarget.style.boxShadow =
                      '0 0 0 2px rgba(25, 118, 210, 0.3)';
                  }}
                  onMouseLeave={(e: React.MouseEvent<HTMLInputElement>) => {
                    e.currentTarget.style.borderColor = '#d5d3d3';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                  style={{
                    width: '100%',
                    height: '40px',
                    padding: '12px 16px',
                    fontSize: '16px',
                    border: '1px solid #ccc',
                    borderRadius: '50px',
                    fontFamily: 'Arial, sans-serif',
                    transition: 'border 0.3s ease',
                    outline: 'none',
                    borderColor: '#d5d3d3',
                    marginBottom: '8px',
                  }}
                />
                <Row>
                  <Input
                    name="crm"
                    value={crmValue}
                    onChange={handleCrmChange}
                    placeholder="CRM"
                    maxLength={9}
                    containerStyle={{ margin: 0 }}
                  />

                  <Select
                    name="uf"
                    options={getBrasilUFs()}
                    placeholder="UF"
                    containerStyle={{ margin: 0 }}
                    ref={selectUFRef}
                  />
                </Row>

                <AcceptTerms>
                  <Checkbox
                    name="acceptanceTerm"
                    options={[
                      {
                        id: '1',
                        value: '1',
                        label: 'Declaro que li e aceito os ',
                      },
                    ]}
                  />

                  <button type="button" onClick={toggleShowAcceptTerm}>
                    termos de consentimento
                  </button>
                </AcceptTerms>
              </FormContent>

              <Actions loading={loading ? 1 : 0}>
                <Button
                  type="submit"
                  loading={loading}
                  loadingMessage={
                    hasCoupon ? 'Validando seu cupom' : 'Realizando cadastro'
                  }
                >
                  OK
                </Button>
              </Actions>
            </CardContent>
          </Form>
        </Card>
      </Animation>

      <LoginArea>
        <Link to="/login">Acessar área restrita</Link>
      </LoginArea>
    </Container>
  );
};

export default Register;
